'use client';

import { useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ChevronLeft, ChevronRight, GripVertical, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { API_ENDPOINTS } from '@/lib/api/config';
import {
  prefetchAuthenticatedBlob,
  useAuthenticatedBlobUrl
} from '@/hooks/use-authenticated-blob-url';

import type { BatchPageRecord } from '../types';
import { useReorderPagesMutation } from '../services/photo-scanning.mutations';

interface RearrangePagesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  batchId: number;
  pages: BatchPageRecord[];
}

export function RearrangePagesDialog({
  open,
  onOpenChange,
  batchId,
  pages
}: RearrangePagesDialogProps) {
  const t = useTranslations('ocr');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='max-h-[85vh] gap-4 sm:max-w-3xl'>
        <DialogHeader>
          <DialogTitle>{t('rearrangePagesTitle')}</DialogTitle>
          <DialogDescription>
            {t('rearrangePagesDescription')}
          </DialogDescription>
        </DialogHeader>

        {open && (
          <RearrangeBody
            batchId={batchId}
            initialPages={pages}
            onClose={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// Mounts only while the dialog is open so the initial order is captured
// once per opening via useState's initializer (no setState-in-effect).
function RearrangeBody({
  batchId,
  initialPages,
  onClose
}: {
  batchId: number;
  initialPages: BatchPageRecord[];
  onClose: () => void;
}) {
  const t = useTranslations('ocr');
  const reorderMutation = useReorderPagesMutation();

  const [orderedPages, setOrderedPages] =
    useState<BatchPageRecord[]>(initialPages);
  const [previewPageId, setPreviewPageId] = useState<number | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const sortableIds = useMemo(
    () => orderedPages.map((p) => p.id),
    [orderedPages]
  );

  const isDirty = useMemo(() => {
    if (orderedPages.length !== initialPages.length) return false;
    return orderedPages.some((p, i) => p.id !== initialPages[i]?.id);
  }, [orderedPages, initialPages]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setOrderedPages((items) => {
      const from = items.findIndex((p) => p.id === active.id);
      const to = items.findIndex((p) => p.id === over.id);
      if (from < 0 || to < 0) return items;
      return arrayMove(items, from, to);
    });
  };

  const handleSave = async () => {
    await reorderMutation.mutateAsync({
      batchId,
      pageIds: orderedPages.map((p) => p.id)
    });
    onClose();
  };

  const previewIndex =
    previewPageId === null
      ? -1
      : orderedPages.findIndex((p) => p.id === previewPageId);
  const previewPage = previewIndex >= 0 ? orderedPages[previewIndex] : null;

  const handlePreviewNav = (direction: -1 | 1) => {
    if (previewIndex < 0) return;
    const next = previewIndex + direction;
    if (next < 0 || next >= orderedPages.length) return;
    setPreviewPageId(orderedPages[next].id);
  };

  // Prefetch neighbor images so prev/next feels instant.
  useEffect(() => {
    if (previewIndex < 0) return;
    for (const offset of [-1, 1]) {
      const neighbor = orderedPages[previewIndex + offset];
      if (!neighbor) continue;
      prefetchAuthenticatedBlob(
        API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(batchId, neighbor.id)
      );
    }
  }, [batchId, orderedPages, previewIndex]);

  return (
    <>
      {orderedPages.length === 0 ? (
        <div className='text-muted-foreground flex h-48 items-center justify-center text-sm'>
          {t('rearrangePagesEmpty')}
        </div>
      ) : (
        <ScrollArea className='max-h-[60vh] rounded-md border'>
          <div className='p-3'>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortableIds}
                strategy={rectSortingStrategy}
              >
                <div className='grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4'>
                  {orderedPages.map((page, index) => (
                    <SortableThumb
                      key={page.id}
                      batchId={batchId}
                      page={page}
                      position={index + 1}
                      onPreview={() => setPreviewPageId(page.id)}
                    />
                  ))}
                </div>
              </SortableContext>
            </DndContext>
          </div>
        </ScrollArea>
      )}

      <DialogFooter>
        <Button
          variant='outline'
          onClick={onClose}
          disabled={reorderMutation.isPending}
        >
          {t('cancel')}
        </Button>
        <Button
          onClick={handleSave}
          disabled={
            !isDirty || reorderMutation.isPending || orderedPages.length === 0
          }
        >
          {reorderMutation.isPending && (
            <Loader2 className='me-2 h-4 w-4 animate-spin' />
          )}
          {t('rearrangePagesSave')}
        </Button>
      </DialogFooter>

      <PagePreviewDialog
        batchId={batchId}
        page={previewPage}
        position={previewIndex + 1}
        total={orderedPages.length}
        hasPrev={previewIndex > 0}
        hasNext={previewIndex >= 0 && previewIndex < orderedPages.length - 1}
        onPrev={() => handlePreviewNav(-1)}
        onNext={() => handlePreviewNav(1)}
        onClose={() => setPreviewPageId(null)}
      />
    </>
  );
}

// ── Sortable thumbnail ─────────────────────────────────────────────────────
function SortableThumb({
  batchId,
  page,
  position,
  onPreview
}: {
  batchId: number;
  page: BatchPageRecord;
  position: number;
  onPreview: () => void;
}) {
  const t = useTranslations('ocr');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id: page.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'bg-card group relative flex flex-col overflow-hidden rounded-md border',
        isDragging && 'ring-primary opacity-60 ring-2'
      )}
    >
      <button
        type='button'
        aria-label='Drag to reorder'
        className='text-muted-foreground hover:bg-muted absolute end-1 top-1 z-10 flex h-6 w-6 cursor-grab items-center justify-center rounded active:cursor-grabbing'
        {...attributes}
        {...listeners}
      >
        <GripVertical className='h-3.5 w-3.5' />
      </button>

      <button
        type='button'
        onClick={onPreview}
        aria-label={t('rearrangePagesPreviewOpen')}
        className='focus-visible:ring-ring block w-full cursor-zoom-in focus:outline-none focus-visible:ring-2'
      >
        <PageThumbnail batchId={batchId} pageId={page.id} />
      </button>

      <div className='flex items-center justify-between gap-2 border-t px-2 py-1.5 text-xs'>
        <span className='font-medium tabular-nums'>{position}</span>
        <span className='text-muted-foreground truncate'>
          {page.page_number}
        </span>
      </div>
    </div>
  );
}

// ── Page preview dialog (full-res image, prev/next nav) ────────────────────
function PagePreviewDialog({
  batchId,
  page,
  position,
  total,
  hasPrev,
  hasNext,
  onPrev,
  onNext,
  onClose
}: {
  batchId: number;
  page: BatchPageRecord | null;
  position: number;
  total: number;
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
  onClose: () => void;
}) {
  const t = useTranslations('ocr');
  const open = page !== null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className='max-h-[90vh] gap-3 p-0 sm:max-w-4xl'
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' && hasPrev) {
            e.preventDefault();
            onPrev();
          } else if (e.key === 'ArrowRight' && hasNext) {
            e.preventDefault();
            onNext();
          }
        }}
      >
        <DialogHeader className='border-b px-4 py-3'>
          <DialogTitle>{t('rearrangePagesPreviewTitle')}</DialogTitle>
          <DialogDescription>
            {t('rearrangePagesPreviewPosition', { position, total })}
          </DialogDescription>
        </DialogHeader>

        <div className='relative flex min-h-[60vh] items-center justify-center bg-black/80 px-12 py-4'>
          {page && <PagePreviewImage batchId={batchId} pageId={page.id} />}

          <Button
            type='button'
            variant='secondary'
            size='icon'
            aria-label={t('rearrangePagesPreviewPrev')}
            onClick={onPrev}
            disabled={!hasPrev}
            className='absolute start-3 top-1/2 -translate-y-1/2 rounded-full shadow-md'
          >
            <ChevronLeft className='h-5 w-5 rtl:hidden' />
            <ChevronRight className='hidden h-5 w-5 rtl:inline' />
          </Button>

          <Button
            type='button'
            variant='secondary'
            size='icon'
            aria-label={t('rearrangePagesPreviewNext')}
            onClick={onNext}
            disabled={!hasNext}
            className='absolute end-3 top-1/2 -translate-y-1/2 rounded-full shadow-md'
          >
            <ChevronRight className='h-5 w-5 rtl:hidden' />
            <ChevronLeft className='hidden h-5 w-5 rtl:inline' />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PagePreviewImage({
  batchId,
  pageId
}: {
  batchId: number;
  pageId: number;
}) {
  const blobUrl = useAuthenticatedBlobUrl(
    API_ENDPOINTS.PHOTO_SCANNING.PAGE_IMAGE(batchId, pageId)
  );

  if (!blobUrl) {
    return (
      <div className='flex h-[60vh] items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-white' />
      </div>
    );
  }

  return (
    /* eslint-disable-next-line @next/next/no-img-element -- in-memory blob URL preview; next/image cannot proxy blob:// schemes */
    <img
      src={blobUrl}
      alt=''
      className='max-h-[75vh] w-auto max-w-full object-contain'
      draggable={false}
    />
  );
}

// ── Thumbnail (cached, ~300px WebP via the shared blob LRU) ────────────────
function PageThumbnail({
  batchId,
  pageId
}: {
  batchId: number;
  pageId: number;
}) {
  const blobUrl = useAuthenticatedBlobUrl(
    API_ENDPOINTS.PHOTO_SCANNING.PAGE_THUMBNAIL(batchId, pageId)
  );

  if (!blobUrl) {
    return (
      <div className='bg-muted/30 flex aspect-[3/4] items-center justify-center'>
        <Loader2 className='text-muted-foreground h-5 w-5 animate-spin' />
      </div>
    );
  }

  return (
    <div className='bg-muted/20 aspect-[3/4] overflow-hidden'>
      {/* eslint-disable-next-line @next/next/no-img-element -- in-memory blob URL preview; next/image cannot proxy blob:// schemes */}
      <img
        src={blobUrl}
        alt=''
        className='h-full w-full object-contain'
        draggable={false}
      />
    </div>
  );
}
